/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: user_organization_roles entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the user_organization_roles table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the user_organization_roles API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [user_id, organization_id, role_id, created_at, created_by, trig_user_email, trig_user_full_name, trig_organization_name, trig_organization_slug, trig_role_name, trig_role_description]
 * Searchable fields: []
 * Filterable fields: [user_id, organization_id, role_id]
 */

import { httpClient } from "../http-client.js";
import type { UnifiedQueryOptions } from "../../../../../config/search/global-search-config.js";
import type {
  UserOrganizationRolesRow,
  UserOrganizationRolesInsert,
  UserOrganizationRolesUpdate,
} from "../../types/generated_schemas/generated-user_organization_roles-schema.js";

export interface UserOrganizationRolesQueryOptions
  extends UnifiedQueryOptions {}

export interface UserOrganizationRolesResponse {
  data: UserOrganizationRolesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for user_organization_roles table operations
 *
 * @description Provides type-safe access to user_organization_roles API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class UserOrganizationRolesClient {
  private readonly basePath = "/user-organization-roles";

  /**
   * Get all user_organization_roles records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of user_organization_roles records
   */
  async getAll(
    options?: UserOrganizationRolesQueryOptions,
  ): Promise<UserOrganizationRolesRow[]> {
    const params = new URLSearchParams();

    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());
    if (options?.sortBy) params.set("sortBy", options.sortBy);
    if (options?.sortOrder) params.set("sortOrder", options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await httpClient.get<UserOrganizationRolesResponse>(url);
    return response.data;
  }

  /**
   * Get a single user_organization_roles record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the user_organization_roles record
   */
  async getById(id: string): Promise<UserOrganizationRolesRow> {
    const response = await httpClient.get<{
      data: UserOrganizationRolesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new user_organization_roles record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created user_organization_roles record
   */
  async create(
    data: UserOrganizationRolesInsert,
  ): Promise<UserOrganizationRolesRow> {
    const response = await httpClient.post<{
      data: UserOrganizationRolesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing user_organization_roles record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated user_organization_roles record
   */
  async update(
    id: string,
    data: UserOrganizationRolesUpdate,
  ): Promise<UserOrganizationRolesRow> {
    const response = await httpClient.put<{
      data: UserOrganizationRolesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a user_organization_roles record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple user_organization_roles records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created user_organization_roles records
   */
  async createMany(
    data: UserOrganizationRolesInsert[],
  ): Promise<UserOrganizationRolesRow[]> {
    const response = await httpClient.post<{
      data: UserOrganizationRolesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple user_organization_roles records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search user_organization_roles with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: UserOrganizationRolesQueryOptions,
  ): Promise<UserOrganizationRolesResponse> {
    throw new Error(
      "Search operations are not available for user_organization_roles - no searchable fields configured",
    );
  }

  /**
   * Get count of user_organization_roles records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString
      ? `${this.basePath}/count?${queryString}`
      : `${this.basePath}/count`;

    const response = await httpClient.get<{ data: number; success: boolean }>(
      url,
    );
    return response.data;
  }

  /**
   * Check if a user_organization_roles record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: "user_organization_roles",
      publicFields: [
        "user_id",
        "organization_id",
        "role_id",
        "created_at",
        "created_by",
        "trig_user_email",
        "trig_user_full_name",
        "trig_organization_name",
        "trig_organization_slug",
        "trig_role_name",
        "trig_role_description",
      ],
      searchableFields: [],
      filterableFields: ["user_id", "organization_id", "role_id"],
      sortableFields: ["created_at"],
      readonlyFields: ["user_id", "organization_id", "role_id", "created_at"],
      requiredFields: ["user_id", "organization_id", "role_id"],
    };
  }
}

// Export singleton instance for convenience
export const userOrganizationRolesClient = new UserOrganizationRolesClient();
